import { FormEvent, useEffect, useMemo, useState } from "react";
import { Pencil, Save, Trash2, X } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import type { CompanyUnit, Maintenance, Vehicle } from "../types";

export function MaintenancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState<Maintenance[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [units, setUnits] = useState<CompanyUnit[]>([]);
  const [unitFilter, setUnitFilter] = useState("");
  const [form, setForm] = useState({ vehicle_id: "", description: "", responsible: "", set_out_of_service: false });
  const [editing, setEditing] = useState<Maintenance | null>(null);
  const [editForm, setEditForm] = useState({ vehicle_id: "", description: "", responsible: "" });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    const [m, v, u] = await Promise.all([
      api<{ maintenances: Maintenance[] }>("maintenance"),
      api<{ vehicles: Vehicle[] }>("vehicles"),
      api<{ units: CompanyUnit[] }>("units")
    ]);
    setItems(m.maintenances);
    setVehicles(v.vehicles);
    setUnits(u.units);
    if (!form.vehicle_id && v.vehicles[0]) setForm((f) => ({ ...f, vehicle_id: v.vehicles[0].id }));
  }
  useEffect(() => { load(); }, []);

  const filteredItems = useMemo(
    () => unitFilter ? items.filter((item) => item.unit_id === unitFilter) : items,
    [items, unitFilter]
  );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    setError("");
    try {
      await api("maintenance", { method: "POST", body: JSON.stringify(form) });
      setForm({ vehicle_id: vehicles[0]?.id || "", description: "", responsible: "", set_out_of_service: false });
      setMessage("Manutenção registrada com sucesso.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível registrar a manutenção.");
    }
  }

  async function complete(id: string) {
    setMessage("");
    setError("");
    try {
      await api("maintenance/" + id + "/complete", { method: "POST" });
      setMessage("Manutenção concluída.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível concluir a manutenção.");
    }
  }

  function openEdit(maintenance: Maintenance) {
    if (!isAdmin) return;
    setEditing(maintenance);
    setEditForm({
      vehicle_id: maintenance.vehicle_id,
      description: maintenance.description,
      responsible: maintenance.responsible || ""
    });
    setMessage("");
    setError("");
  }

  async function saveEdit(event: FormEvent) {
    event.preventDefault();
    if (!editing || !isAdmin || saving) return;

    setSaving(true);
    setMessage("");
    setError("");
    try {
      await api("maintenance/" + editing.id, {
        method: "PATCH",
        body: JSON.stringify({
          vehicle_id: editForm.vehicle_id,
          description: editForm.description.trim(),
          responsible: editForm.responsible.trim() || null
        })
      });
      setEditing(null);
      setMessage("Manutenção atualizada. A alteração foi registrada na auditoria.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível editar a manutenção.");
    } finally {
      setSaving(false);
    }
  }

  async function removeMaintenance(maintenance: Maintenance) {
    if (!isAdmin) return;

    const reason = window.prompt(
      "Informe o motivo da exclusão deste registro de manutenção:",
      "Registro de teste ou lançamento incorreto"
    );
    if (reason === null) return;

    const confirmed = window.confirm(
      "Excluir esta manutenção da operação?\n\n" +
      "Ela deixará de aparecer na aba Manutenção, mas a exclusão ficará registrada na auditoria. " +
      "O status do veículo não será alterado automaticamente."
    );
    if (!confirmed) return;

    setMessage("");
    setError("");
    try {
      await api("maintenance/" + maintenance.id, {
        method: "DELETE",
        body: JSON.stringify({ reason: reason.trim() || "Exclusão administrativa" })
      });
      setMessage("Manutenção excluída da operação. A ação permaneceu registrada na auditoria.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Não foi possível excluir a manutenção.");
    }
  }

  return (
    <section>
      <div className="page-title">
        <div>
          <h1>Manutenção</h1>
          <p>Registro de intervenções e indisponibilidade explícita.</p>
        </div>
      </div>

      {message && <div className="alert">{message}</div>}
      {error && <div className="alert error">{error}</div>}

      <form className="panel form-grid" onSubmit={submit}>
        <label>Veículo
          <select value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })}>
            {vehicles.map((v) => <option key={v.id} value={v.id}>{v.identifier}{v.unit_name ? " · " + v.unit_name : ""}</option>)}
          </select>
        </label>
        <label>Responsável
          <input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} />
        </label>
        <label className="full-span">Descrição
          <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </label>
        <label className="check full-span">
          <input type="checkbox" checked={form.set_out_of_service} onChange={(e) => setForm({ ...form, set_out_of_service: e.target.checked })} />
          Marcar veículo como “Em manutenção”
        </label>
        <div className="form-actions"><button className="primary">Registrar manutenção</button></div>
      </form>

      <div className="panel unit-filter-bar">
        <div>
          <span className="section-kicker">UNIDADE</span>
          <strong>Filtrar manutenções</strong>
        </div>
        <select value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
          <option value="">Todas as unidades</option>
          {units.map((unit) => <option key={unit.id} value={unit.id}>{unit.name}</option>)}
        </select>
      </div>

      <div className="panel maintenance-list-panel">
        <div className="table-panel-heading">
          <div>
            <span className="section-kicker">HISTÓRICO</span>
            <h2>Registros de manutenção</h2>
          </div>
          <small>{filteredItems.length} registro(s)</small>
        </div>

        {filteredItems.map((m) => (
          <article className="list-card maintenance-card" key={m.id}>
            <div className="maintenance-card-copy">
              <div className="row-gap">
                <b>{m.vehicle_identifier}</b>
                <span className="unit-badge">{m.unit_name || "Unidade não definida"}</span>
                <span className={"status " + (m.status === "aberta" ? "ressalva" : "liberado")}>
                  {m.status === "aberta" ? "Aberta" : "Concluída"}
                </span>
              </div>
              <p>{m.description}</p>
              <small>
                {m.responsible || "Sem responsável informado"} · {new Date(m.started_at).toLocaleString("pt-BR")}
                {m.completed_at ? " · Concluída em " + new Date(m.completed_at).toLocaleString("pt-BR") : ""}
              </small>
            </div>

            <div className="actions maintenance-card-actions">
              {m.status === "aberta" && (
                <button className="secondary" type="button" onClick={() => complete(m.id)}>
                  Concluir
                </button>
              )}

              {isAdmin && (
                <>
                  <button className="secondary icon-action" type="button" onClick={() => openEdit(m)}>
                    <Pencil size={15} /> Editar
                  </button>
                  <button className="danger-outline icon-action" type="button" onClick={() => removeMaintenance(m)}>
                    <Trash2 size={15} /> Excluir
                  </button>
                </>
              )}
            </div>
          </article>
        ))}

        {!items.length && <p className="empty">Nenhuma manutenção registrada.</p>}
        {items.length > 0 && !filteredItems.length && <p className="empty">Nenhuma manutenção encontrada para esta unidade.</p>}
      </div>

      {editing && isAdmin && (
        <div className="modal-backdrop" onClick={() => !saving && setEditing(null)}>
          <form className="modal maintenance-edit-modal" onSubmit={saveEdit} onClick={(event) => event.stopPropagation()}>
            <div className="modal-title-row">
              <div>
                <span className="section-kicker">ADMINISTRAÇÃO</span>
                <h2>Editar manutenção</h2>
              </div>
              <button className="modal-close" type="button" onClick={() => setEditing(null)} aria-label="Fechar">
                <X size={18} />
              </button>
            </div>

            <label>Veículo
              <select
                required
                value={editForm.vehicle_id}
                onChange={(e) => setEditForm({ ...editForm, vehicle_id: e.target.value })}
              >
                {vehicles.filter((v) => v.active === 1).map((v) => (
                  <option key={v.id} value={v.id}>{v.identifier}</option>
                ))}
              </select>
            </label>

            <label>Responsável
              <input
                value={editForm.responsible}
                onChange={(e) => setEditForm({ ...editForm, responsible: e.target.value })}
              />
            </label>

            <label>Descrição
              <textarea
                required
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              />
            </label>

            <p className="muted maintenance-edit-note">
              A edição não altera automaticamente o status da manutenção nem o status operacional do veículo.
              A alteração ficará registrada na trilha de auditoria.
            </p>

            <div className="modal-actions">
              <button className="secondary" type="button" onClick={() => setEditing(null)} disabled={saving}>Cancelar</button>
              <button className="primary" type="submit" disabled={saving}>
                <Save size={16} /> {saving ? "Salvando..." : "Salvar alterações"}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
